import React from 'react';
import Modal from '../components/Modal';
import { formatDate } from '../utils/formatUtils';

const InterviewReadinessStudentViewModal = ({
  isOpen,
  enrollment,
  readiness,
  onClose,
  getReadinessStatus
}) => {
  const boolLabel = (value) => (value ? 'Yes' : 'No');
  const triStateLabel = (value) => (value === null || value === undefined ? 'Not Set' : boolLabel(value));

  return (
    <Modal
      isOpen={isOpen && !!enrollment}
      onClose={onClose}
      title={`Student Details - ${enrollment?.student?.fullName || 'Student'}`}
      size="xl"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold theme-text mb-3">Student Information</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium theme-text-secondary">Name:</span> <span className="ml-2 theme-text">{enrollment?.student?.fullName || 'N/A'}</span></div>
              <div><span className="font-medium theme-text-secondary">Student ID:</span> <span className="ml-2 theme-text">{enrollment?.student?.id || 'N/A'}</span></div>
              <div><span className="font-medium theme-text-secondary">Email:</span> <span className="ml-2 theme-text">{enrollment?.student?.emailAddress || 'N/A'}</span></div>
              <div><span className="font-medium theme-text-secondary">Phone:</span> <span className="ml-2 theme-text">{enrollment?.student?.phoneNumber || 'N/A'}</span></div>
              <div><span className="font-medium theme-text-secondary">Domain:</span> <span className="ml-2 theme-text">{enrollment?.student?.domain?.domainName || 'N/A'}</span></div>
            </div>
          </div>

          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold theme-text mb-3">Enrollment Information</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium theme-text-secondary">Batch:</span> <span className="ml-2 theme-text">{enrollment?.batch?.batchName || 'N/A'}</span></div>
              <div><span className="font-medium theme-text-secondary">Course:</span> <span className="ml-2 theme-text">{enrollment?.batch?.course?.courseName || 'N/A'}</span></div>
              <div><span className="font-medium theme-text-secondary">Enrollment Date:</span> <span className="ml-2 theme-text">{formatDate(enrollment?.enrollmentDate) || 'N/A'}</span></div>
              <div>
                <span className="font-medium theme-text-secondary">Readiness Status:</span>
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getReadinessStatus(enrollment?.id).color}`}>
                  {getReadinessStatus(enrollment?.id).label}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold theme-text mb-3">Readiness Checklist</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div><span className="font-medium theme-text-secondary">In Placement Group:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.inPlacementGroup)}</span></div>
            <div><span className="font-medium theme-text-secondary">In Mock Group:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.inMockGroup)}</span></div>
            <div><span className="font-medium theme-text-secondary">In Underprepared Group:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.inUnderpreparedGroup)}</span></div>
            <div><span className="font-medium theme-text-secondary">All Modules Complete:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.allModulesComplete)}</span></div>
            <div><span className="font-medium theme-text-secondary">Project Submitted:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.projectSubmitted)}</span></div>
            <div><span className="font-medium theme-text-secondary">Resume Ready & Submitted:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.resumeReadySubmitted)}</span></div>
            <div><span className="font-medium theme-text-secondary">Mock Interview Done:</span> <span className="ml-2 theme-text">{boolLabel(readiness?.mockInterviewDone)}</span></div>
            <div><span className="font-medium theme-text-secondary">Number of Mock Interviews:</span> <span className="ml-2 theme-text">{readiness?.numberOfMockInterviews ?? 0}</span></div>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold theme-text mb-3">Readiness Assessment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><span className="font-medium theme-text-secondary">Student Readiness:</span> <span className="ml-2 theme-text">{triStateLabel(readiness?.studentReadiness)}</span></div>
            <div><span className="font-medium theme-text-secondary">Trainer Readiness:</span> <span className="ml-2 theme-text">{triStateLabel(readiness?.trainerReadiness)}</span></div>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold theme-text mb-3">Resume Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div><span className="font-medium theme-text-secondary">Resume Submission Deadline:</span> <span className="ml-2 theme-text">{formatDate(readiness?.resumeSubmissionDeadline) || 'N/A'}</span></div>
            <div><span className="font-medium theme-text-secondary">Resume Reviewed:</span> <span className="ml-2 theme-text">{triStateLabel(readiness?.resumeReviewed)}</span></div>
            <div><span className="font-medium theme-text-secondary">Resume Changes Needed:</span> <span className="ml-2 theme-text">{triStateLabel(readiness?.resumeChangesNeeded)}</span></div>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold theme-text mb-3">Ratings (1-5)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><span className="font-medium theme-text-secondary">Communication Skills:</span> <span className="ml-2 theme-text">{readiness?.communicationSkillsRating ?? 'N/A'}</span></div>
            <div><span className="font-medium theme-text-secondary">Technical Readiness:</span> <span className="ml-2 theme-text">{readiness?.technicalReadinessRating ?? 'N/A'}</span></div>
            <div><span className="font-medium theme-text-secondary">Attitude:</span> <span className="ml-2 theme-text">{readiness?.attitudeRating ?? 'N/A'}</span></div>
            <div><span className="font-medium theme-text-secondary">Presentation:</span> <span className="ml-2 theme-text">{readiness?.presentationRating ?? 'N/A'}</span></div>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold theme-text mb-3">Additional Information</h3>
          <div className="space-y-2 text-sm">
            <div><span className="font-medium theme-text-secondary">Areas to Improve:</span> <span className="ml-2 theme-text">{readiness?.areasToImprove || 'N/A'}</span></div>
            <div><span className="font-medium theme-text-secondary">Feedback Score:</span> <span className="ml-2 theme-text">{readiness?.feedbackScore || 'N/A'}</span></div>
            <div><span className="font-medium theme-text-secondary">Interview Ready:</span> <span className="ml-2 theme-text">{triStateLabel(readiness?.interviewReady)}</span></div>
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-medium theme-button-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default InterviewReadinessStudentViewModal;
